/**
 * "Đổi tên / chuyển nhượng": opens a `.vn` name-change request from the domain page.
 *
 * Owner decision 2026-09-29: when the current holder is an organisation, "Đổi tên tổ chức (giữ mã
 * số doanh nghiệp)" (SUBJECT_CONVERSION / AUTHORITY_RENAME) comes first; a name correction is
 * offered only as a spelling correction, with a reason. An individual never gets the rename.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api, isApiError, type DomainDetail } from '../../api';
import { NAME_CHANGE_SUB_CASES, type NameChangeCase, type NameChangeSubCase } from '../../api/areas/namechange';
import { Callout, Field } from '../../components/blocks';
import { useWrite, WriteError } from '../../components/Write';
import { useApi } from '../../lib/useApi';
import { ReasonField, selectClass } from '../operator/shared';
import { useEnum } from './shared';

/** What the picker offers: the cases, plus the organisation rename as its own choice. */
type Choice = 'ORG_RENAME' | NameChangeCase;

/** The choices for a holder: an organisation's rename first; an individual as before. */
function nameChangeChoices(organisation: boolean): Choice[] {
  return organisation ? ['ORG_RENAME', 'HOLDER_TRANSFER', 'SUBJECT_CONVERSION', 'NAME_CORRECTION'] : ['HOLDER_TRANSFER', 'SUBJECT_CONVERSION', 'NAME_CORRECTION'];
}

/** The case / sub-case a choice opens. */
function caseOf(choice: Choice, sub: NameChangeSubCase): { case: NameChangeCase; sub_case?: NameChangeSubCase } {
  if (choice === 'ORG_RENAME') return { case: 'SUBJECT_CONVERSION', sub_case: 'AUTHORITY_RENAME' };
  return choice === 'SUBJECT_CONVERSION' ? { case: choice, sub_case: sub } : { case: choice };
}

export function NameChangeForm({ d, onClose, onChanged }: { d: DomainDetail; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  const subjects = useApi((signal) => api.listSubjects({ customer_id: d.domain.customer_id, limit: 100 }, { signal }), [d.domain.customer_id]);
  const list = subjects.status === 'success' ? subjects.data.items : [];
  const holder = list.find((s) => s.id === d.domain.current_subject_id);
  const organisation = !!holder && holder.subject_type !== 'INDIVIDUAL';
  const [picked, setPicked] = useState<Choice | null>(null);
  const choice: Choice = picked ?? (organisation ? 'ORG_RENAME' : 'HOLDER_TRANSFER');
  // The rename keeping the enterprise code is its own choice (organisations only).
  const subCases = NAME_CHANGE_SUB_CASES.filter((c) => c !== 'AUTHORITY_RENAME');
  const [sub, setSub] = useState<NameChangeSubCase>('GIFT_CONTRIBUTION_INHERITANCE');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [spelling, setSpelling] = useState('');
  const [reason, setReason] = useState('');
  const spellingNeeded = organisation && choice === 'NAME_CORRECTION';
  const w = useWrite((_: null, opts) =>
    api.openNameChange(
      d.domain.id,
      { ...caseOf(choice, sub), to_subject_id: to, note: note.trim() || undefined, spelling_correction_reason: spellingNeeded ? spelling.trim() : undefined, reason: reason.trim() },
      opts,
    ),
  );
  const created = w.state.phase === 'done' ? w.state.result : null;
  const err = w.state.phase === 'error' ? w.state.error : null;
  const errCode = isApiError(err) ? err.code : null;
  // A name correction / rename keeps the holder (a new version of the current subject), or — for
  // the rename — another subject with the same enterprise code; a transfer goes to another one.
  const choices =
    choice === 'NAME_CORRECTION'
      ? list.filter((s) => s.id === d.domain.current_subject_id)
      : choice === 'HOLDER_TRANSFER'
        ? list.filter((s) => s.id !== d.domain.current_subject_id)
        : choice === 'ORG_RENAME'
          ? list.filter((s) => s.subject_type !== 'INDIVIDUAL')
          : list;
  const toHint = choice === 'NAME_CORRECTION' ? 'nameChange.toSubjectSame' : choice === 'ORG_RENAME' ? 'nameChange.toSubjectRename' : 'nameChange.toSubjectHint';
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && onChanged());
      }}
    >
      {created ? (
        <Callout tone="ok">
          {t('nameChange.opened')}{' '}
          <Link to={`/name-changes/${created.id}`} className="font-medium text-primary hover:underline">
            {t('nameChange.openRequest')}
          </Link>
        </Callout>
      ) : (
        <>
          <Callout tone="info">{t('nameChange.formHint')}</Callout>
          <Field label={t('nameChange.case')}>
            <select className={selectClass} value={choice} onChange={(ev) => setPicked(ev.target.value as Choice)}>
              {nameChangeChoices(organisation).map((c) => (
                <option key={c} value={c}>
                  {t(`nameChange.choice.${c === 'NAME_CORRECTION' && organisation ? 'NAME_CORRECTION_ORG' : c}`)}
                </option>
              ))}
            </select>
          </Field>
          {choice === 'ORG_RENAME' ? <Callout tone="info">{t('nameChange.orgRenameHint')}</Callout> : null}
          {spellingNeeded ? <Callout tone="warn">{t('nameChange.spellingOnlyHint')}</Callout> : null}
          {choice === 'SUBJECT_CONVERSION' ? (
            <Field label={t('nameChange.subCase')}>
              <select className={selectClass} value={sub} onChange={(ev) => setSub(ev.target.value as NameChangeSubCase)}>
                {subCases.map((c) => (
                  <option key={c} value={c}>
                    {e('ncSubCase', c)}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          <Field label={t('nameChange.toSubject')} hint={t(toHint)}>
            <select className={selectClass} value={to} onChange={(ev) => setTo(ev.target.value)} required>
              <option value="">{subjects.status === 'loading' ? t('nameChange.loading') : t('nameChange.choose')}</option>
              {choices.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.legal_name ?? s.id}
                </option>
              ))}
            </select>
          </Field>
          {spellingNeeded ? (
            <Field label={t('nameChange.spellingReason')} hint={t('nameChange.spellingReasonHint')}>
              <Textarea value={spelling} onChange={(ev) => setSpelling(ev.target.value)} rows={2} maxLength={1000} required />
            </Field>
          ) : null}
          <Field label={t('nameChange.note')}>
            <Textarea value={note} onChange={(ev) => setNote(ev.target.value)} rows={2} maxLength={2000} />
          </Field>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {errCode === 'NAME_CHANGE_CASE_MISMATCH' ? (
        <Callout tone="warn">{t('nameChange.caseMismatch')}</Callout>
      ) : errCode === 'ENTERPRISE_CODE_CHANGED' ? (
        <Callout tone="warn">{t('nameChange.enterpriseChanged')}</Callout>
      ) : err ? (
        <WriteError error={err} />
      ) : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('nameChange.close')}
        </Button>
        {created ? null : (
          <Button type="submit" disabled={w.busy || !to || !reason.trim() || (spellingNeeded && !spelling.trim())}>
            {t('nameChange.submitOpen')}
          </Button>
        )}
      </DialogFooter>
    </form>
  );
}
